import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PageHeader, QuickFilters, InlineFilters, DataTable, Tag, Button, Dialog, ConfirmDialog, FileDrop, Banner, Segmented, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { AudioRecorder } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { apiError } from '../../next/ui';

/**
 * קטלוג קריינות — new design. Same endpoints as NarrationCatalog (/narration-prompts…):
 * list, play, upload (file / drag & drop / recording), switch file ⇄ TTS (deletes the file), sync with Yemot.
 */
const MODE_LABELS = { file: 'קובץ קול', tts: 'TTS' };
const STATUS_LABELS = { active: 'פעיל', unused: 'לא בשימוש' };
const AUDIO_EXT_RE = /\.(mp3|wav|ogg|m4a|aac|flac|wma|webm)$/i;
const isAudioFile = (f) => !!f && (f.type?.startsWith('audio/') || AUDIO_EXT_RE.test(f.name || ''));
const fileSize = (b) => (b == null ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const hasFile = (r) => !!(r.fileExistsOnYemot || r.audioFilename || r.hasAudioData);

async function blobError(data, fallback) {
  if (data instanceof Blob) {
    try { const j = JSON.parse(await data.text()); return j?.error || fallback; } catch { return fallback; }
  }
  return data?.error || fallback;
}

export default function NarrationCatalogNext() {
  const toast = useCkToast();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [quick, setQuick] = useState('active');
  const [modeFilter, setModeFilter] = useState('');
  const [yemotFilter, setYemotFilter] = useState('');
  const [extensionFilter, setExtensionFilter] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [confirmSwitch, setConfirmSwitch] = useState(null);
  const [audioRow, setAudioRow] = useState(null); // upload / record dialog
  const [audioTab, setAudioTab] = useState('file');
  const [pendingFile, setPendingFile] = useState(null);
  const audioElRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get('/narration-prompts', { params: { all: '1', sort: 'code', order: 'asc' } });
      setAllData(res.data || []);
    } catch (e) {
      setError(apiError(e, 'שגיאה בשליפת קטלוג קריינות'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => () => { audioElRef.current?.pause(); audioElRef.current = null; }, []);
  useEffect(() => () => { if (pendingFile?.url) URL.revokeObjectURL(pendingFile.url); }, [pendingFile]);

  const extensions = useMemo(() => [...new Set(allData.map((r) => r.extension).filter(Boolean))].sort(), [allData]);
  const counts = useMemo(() => ({
    all: allData.length,
    active: allData.filter((r) => r.status === 'active').length,
    unused: allData.filter((r) => r.status === 'unused').length,
    missing: allData.filter((r) => r.mode === 'file' && !r.fileExistsOnYemot).length,
    tts: allData.filter((r) => r.mode === 'tts').length,
  }), [allData]);
  const rows = useMemo(() => allData.filter((r) => {
    if (quick === 'active' && r.status !== 'active') return false;
    if (quick === 'unused' && r.status !== 'unused') return false;
    if (quick === 'missing' && !(r.mode === 'file' && !r.fileExistsOnYemot)) return false;
    if (quick === 'tts' && r.mode !== 'tts') return false;
    if (modeFilter && r.mode !== modeFilter) return false;
    if (extensionFilter && r.extension !== extensionFilter) return false;
    if (yemotFilter === 'yes' && !r.fileExistsOnYemot) return false;
    if (yemotFilter === 'no' && r.fileExistsOnYemot) return false;
    return true;
  }), [allData, quick, modeFilter, extensionFilter, yemotFilter]);

  const stopPlay = () => { audioElRef.current?.pause(); audioElRef.current = null; setPlayingId(null); };
  const handlePlay = useCallback(async (id) => {
    if (playingId === id && audioElRef.current) { stopPlay(); return; }
    stopPlay();
    try {
      const { data: blob, status } = await api.get(`/narration-prompts/${id}/audio`, { responseType: 'blob' });
      if (blob.type?.includes('application/json') || status >= 400) { toast.error(await blobError(blob, 'לא ניתן להשמיע את הקובץ')); return; }
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); audioElRef.current = null; };
      audio.onerror = () => { setPlayingId(null); URL.revokeObjectURL(url); audioElRef.current = null; toast.error('הדפדפן לא הצליח לנגן את הקובץ'); };
      audioElRef.current = audio;
      setPlayingId(id);
      audio.play().catch(() => { setPlayingId(null); toast.error('הדפדפן חסם את הנגינה'); });
    } catch (e) {
      toast.error(await blobError(e.response?.data, 'שגיאה בהשמעת הקובץ'));
      setPlayingId(null);
    }
  }, [playingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = useCallback(async (id, file) => {
    if (!isAudioFile(file)) { toast.error("סוג קובץ לא נתמך — יש להעלות קובץ שמע (mp3, wav, ogg, m4a, webm וכו')"); return false; }
    const fd = new FormData();
    fd.append('audio', file);
    setUploadingId(id);
    try {
      const { data: res } = await api.post(`/narration-prompts/${id}/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAllData((prev) => prev.map((r) => (r._id === id ? {
        ...r, audioFilename: res.audioFilename, yemotPath: res.yemotPath, mode: res.mode || 'file', hasAudioData: true, fileExistsOnYemot: Boolean(res.fileExistsOnYemot),
      } : r)));
      toast.success('קובץ הקול הועלה');
      return true;
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהעלאת קובץ שמע'));
      return false;
    } finally {
      setUploadingId(null);
    }
  }, [toast]);

  const openAudio = (row, tab = 'file') => { setPendingFile(null); setAudioTab(tab); setAudioRow(row); };
  const closeAudio = () => { if (uploadingId === audioRow?._id) return; setPendingFile(null); setAudioRow(null); };
  const pickFile = (file) => {
    if (!isAudioFile(file)) { toast.error("סוג קובץ לא נתמך — יש לגרור קובץ שמע (mp3, wav, ogg, m4a, webm וכו')"); return; }
    setPendingFile({ file, url: URL.createObjectURL(file) });
  };
  const confirmUpload = async () => {
    if (!audioRow || !pendingFile) return;
    if (await upload(audioRow._id, pendingFile.file)) { setPendingFile(null); setAudioRow(null); }
  };
  const saveRecording = async (blob) => {
    if (!blob || !audioRow) return;
    const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([blob], `recording-${audioRow.code || audioRow._id}.${ext}`, { type: blob.type || `audio/${ext}` });
    if (await upload(audioRow._id, file)) setAudioRow(null);
  };

  const switchToTts = async (row) => {
    try {
      if (row.audioFilename || row.hasAudioData) {
        await api.delete(`/narration-prompts/${row._id}/audio`);
        setAllData((prev) => prev.map((r) => (r._id === row._id ? { ...r, mode: 'tts', yemotPath: '', audioFilename: '', hasAudioData: false, fileExistsOnYemot: false } : r)));
      } else {
        const { data } = await api.patch(`/narration-prompts/${row._id}`, { mode: 'tts' });
        setAllData((prev) => prev.map((r) => (r._id === row._id ? { ...r, ...data } : r)));
      }
      stopPlay();
      toast.success(`מקטע ${row.code} עבר ל-TTS`);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במעבר ל-TTS'));
    } finally {
      setConfirmSwitch(null);
    }
  };

  const switchToFile = async (row) => {
    if (!hasFile(row)) { openAudio(row, 'file'); return; }
    try {
      const { data } = await api.patch(`/narration-prompts/${row._id}`, { mode: 'file' });
      setAllData((prev) => prev.map((r) => (r._id === row._id ? { ...r, ...data } : r)));
      toast.success(`מקטע ${row.code} עבר לקובץ קול`);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במעבר לקובץ קול'));
    }
  };

  const syncYemot = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/narration-prompts/sync-yemot');
      toast.success(`סנכרון הושלם: ${data.toFile} קבצים בימות, ${data.toTts} הועברו ל-TTS, ${data.yemotFileCount} קבצים בתיקיית audio`, { duration: 8000 });
      await loadData();
    } catch (e) {
      toast.error(apiError(e, 'סנכרון נכשל'));
    } finally {
      setSyncing(false);
    }
  };

  const columns = useMemo(() => [
    { key: 'code', label: 'קוד', type: 'number', width: 90, render: (r) => <span className="nx-num">{r.code}</span> },
    { key: 'text', label: 'טקסט', width: 360, render: (r) => <span style={{ whiteSpace: 'normal' }}>{r.text || '—'}</span> },
    { key: 'mode', label: 'סוג', type: 'enum', getValue: (r) => MODE_LABELS[r.mode] || r.mode, render: (r) => <Tag tone={r.mode === 'file' ? 'primary' : 'neutral'}>{MODE_LABELS[r.mode] || r.mode}</Tag> },
    {
      key: 'fileExistsOnYemot',
      label: 'בימות',
      type: 'enum',
      getValue: (r) => (r.mode === 'tts' && !r.fileExistsOnYemot ? 'TTS' : r.fileExistsOnYemot ? 'קיים' : 'חסר'),
      render: (r) => (r.mode === 'tts' && !r.fileExistsOnYemot ? <Tag>TTS</Tag>
        : r.fileExistsOnYemot ? <Tag tone="success" icon="check">קיים</Tag> : <Tag tone="warning" icon="alert">חסר</Tag>),
    },
    { key: 'extension', label: 'שלוחה', type: 'enum', ltr: true },
    { key: 'status', label: 'סטטוס', type: 'enum', getValue: (r) => STATUS_LABELS[r.status] || r.status, render: (r) => <Tag tone={r.status === 'active' ? 'success' : 'neutral'}>{STATUS_LABELS[r.status] || r.status}</Tag> },
    { key: 'notes', label: 'הערות' },
  ], []);

  return (
    <>
      <PageHeader
        title="קטלוג קריינות"
        sub={`${rows.length.toLocaleString('he-IL')} מקטעים מוצגים · ${counts.all.toLocaleString('he-IL')} בסך הכול`}
        more={[{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: loadData }]}
      >
        <Button variant="primary" icon="refresh" disabled={syncing} onClick={syncYemot}>{syncing ? 'מסנכרן…' : 'סנכרן מול ימות'}</Button>
      </PageHeader>

      <QuickFilters
        value={quick}
        onChange={(k) => setQuick((cur) => (cur === k ? 'all' : k))}
        loading={loading && !allData.length}
        items={[
          { key: 'all', label: 'כל המקטעים', value: counts.all, icon: 'list', tone: 'neutral' },
          { key: 'active', label: 'פעילים', value: counts.active, icon: 'check', tone: 'success' },
          { key: 'unused', label: 'לא בשימוש', value: counts.unused, icon: 'pause', tone: 'neutral' },
          { key: 'missing', label: 'קובץ חסר בימות', value: counts.missing, icon: 'alert', tone: 'warning', warn: counts.missing > 0 },
          { key: 'tts', label: 'TTS', value: counts.tts, icon: 'mic', tone: 'info' },
        ]}
      />
      <InlineFilters
        items={[
          { key: 'mode', label: 'סוג', type: 'chips', value: modeFilter, onChange: setModeFilter, options: [{ value: '', label: 'הכל' }, { value: 'file', label: 'קובץ קול' }, { value: 'tts', label: 'TTS' }] },
          { key: 'yemot', label: 'בימות', type: 'chips', value: yemotFilter, onChange: setYemotFilter, options: [{ value: '', label: 'הכל' }, { value: 'yes', label: 'קיים' }, { value: 'no', label: 'חסר' }] },
          { key: 'ext', label: 'שלוחה', type: 'select', value: extensionFilter, onChange: setExtensionFilter, placeholder: 'כל השלוחות', options: extensions.map((x) => ({ value: x, label: x })) },
        ]}
      />

      <DataTable
        mode="client"
        columns={columns}
        rows={rows}
        rowKey="_id"
        loading={loading}
        error={error}
        onRetry={loadData}
        onRefresh={loadData}
        selectable={false}
        storageKey="bchinot-narration"
        defaultQuery={{ pageSize: 100, sort: { key: 'code', dir: 1 } }}
        searchPlaceholder="חיפוש לפי קוד, טקסט או הערה…"
        exportFilename="קטלוג-קריינות"
        emptyTitle="אין מקטעי קריינות"
        emptyText="יש להריץ seedNarrationPrompts, או לשנות את הסינון"
        actionsWidth={130}
        onRowClick={(r) => openAudio(r, 'file')}
        rowActions={(r) => [
          hasFile(r) ? { key: 'play', icon: playingId === r._id ? 'pause' : 'play', label: playingId === r._id ? 'עצירה' : 'השמעה', onClick: () => handlePlay(r._id) } : null,
          { key: 'upload', icon: 'upload', label: 'העלאה / החלפה של קובץ קול', onClick: () => openAudio(r, 'file') },
          { key: 'rec', icon: 'mic', label: 'הקלטה', onClick: () => openAudio(r, 'record') },
        ].filter(Boolean)}
        rowMenu={(r) => [
          r.mode === 'file'
            ? { key: 'tts', label: 'החלפה ל-TTS', icon: 'swap', onClick: () => setConfirmSwitch(r) }
            : { key: 'file', label: r.fileExistsOnYemot ? 'החלפה לקובץ קול' : 'העלאת קובץ כדי לעבור לקובץ קול', icon: 'swap', onClick: () => switchToFile(r) },
          (r.audioFilename || r.hasAudioData) ? { divider: true } : null,
          (r.audioFilename || r.hasAudioData) ? { key: 'del', label: 'מחיקת הקובץ וחזרה ל-TTS', icon: 'trash', danger: true, onClick: () => setConfirmSwitch(r) } : null,
        ].filter(Boolean)}
        renderMobileRow={(r) => (
          <div className="nx-item__main">
            <b><span className="nx-num">{r.code}</span> {r.text}</b>
            <small>{[MODE_LABELS[r.mode], r.extension, STATUS_LABELS[r.status]].filter(Boolean).join(' · ')}</small>
          </div>
        )}
      />

      <ConfirmDialog
        open={!!confirmSwitch}
        onClose={() => setConfirmSwitch(null)}
        onConfirm={() => confirmSwitch && switchToTts(confirmSwitch)}
        title="החלפה ל-TTS"
        message={<p className="ck-confirm__msg">{`להחליף את מקטע ${confirmSwitch?.code} ל-TTS${(confirmSwitch?.audioFilename || confirmSwitch?.hasAudioData) ? ' ולמחוק את קובץ הקול?' : '?'}`}</p>}
        confirmLabel="אישור"
      />

      <Dialog
        open={!!audioRow}
        onClose={closeAudio}
        title={`קובץ קול לקוד ${audioRow?.code ?? ''}`}
        icon="mic"
        size="md"
        footer={audioTab === 'file' && pendingFile ? (
          <>
            <Button variant="ghost" disabled={uploadingId === audioRow?._id} onClick={() => setPendingFile(null)}>בחירת קובץ אחר</Button>
            <Button variant="primary" icon="upload" disabled={uploadingId === audioRow?._id} onClick={confirmUpload}>{uploadingId === audioRow?._id ? 'מעלה…' : 'אשר העלאה'}</Button>
          </>
        ) : <Button variant="ghost" disabled={uploadingId === audioRow?._id} onClick={closeAudio}>סגירה</Button>}
      >
        <div className="nx-stack">
          {audioRow?.text ? <Banner tone="info" icon="note">{audioRow.text}</Banner> : null}
          <Segmented
            value={audioTab}
            onChange={(v) => { setAudioTab(v); setPendingFile(null); }}
            items={[{ value: 'file', label: 'העלאת קובץ' }, { value: 'record', label: 'הקלטה' }]}
            ariaLabel="מקור הקול"
          />
          {audioTab === 'file' ? (
            pendingFile ? (
              <div className="nx-stack" style={{ gap: 8 }}>
                <div className="nx-item is-on">
                  <div className="nx-item__main"><b>{pendingFile.file.name}</b><small>{fileSize(pendingFile.file.size)}</small></div>
                </div>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio controls src={pendingFile.url} style={{ width: '100%' }} />
              </div>
            ) : (
              <FileDrop accept="audio/*" onFile={pickFile} loading={uploadingId === audioRow?._id} title="גררו קובץ שמע לכאן, או לחצו לבחירה" hint="mp3 · wav · ogg · m4a · webm — יוחלף הקובץ הקיים" icon="upload" />
            )
          ) : audioRow ? (
            <AudioRecorder key={audioRow._id} onSave={saveRecording} saving={uploadingId === audioRow._id} disabled={uploadingId === audioRow._id} />
          ) : null}
        </div>
      </Dialog>
    </>
  );
}
